/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { riskApprovalsDecide } from "../funcs/riskApprovalsDecide.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { RiskPolicyApproval } from "../models/components/riskpolicyapproval.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  DecideRiskPolicyApprovalRequest,
  DecideRiskPolicyApprovalSecurity,
} from "../models/operations/decideriskpolicyapproval.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type RiskDecideApprovalMutationVariables = {
  request: DecideRiskPolicyApprovalRequest;
  security?: DecideRiskPolicyApprovalSecurity | undefined;
  options?: RequestOptions;
};

export type RiskDecideApprovalMutationData = RiskPolicyApproval;

export type RiskDecideApprovalMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * decideRiskPolicyApproval risk
 *
 * @remarks
 * Approve or deny a pending tool approval request. Only a listed approver may decide, and never the person who made the call. An approval releases one retry of the exact call.
 */
export function useRiskDecideApprovalMutation(
  options?: MutationHookOptions<
    RiskDecideApprovalMutationData,
    RiskDecideApprovalMutationError,
    RiskDecideApprovalMutationVariables
  >,
): UseMutationResult<
  RiskDecideApprovalMutationData,
  RiskDecideApprovalMutationError,
  RiskDecideApprovalMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildRiskDecideApprovalMutation(client, options),
    ...options,
  });
}

export function mutationKeyRiskDecideApproval(): MutationKey {
  return ["@gram/client", "approvals", "decide"];
}

export function buildRiskDecideApprovalMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: RiskDecideApprovalMutationVariables,
  ) => Promise<RiskDecideApprovalMutationData>;
} {
  return {
    mutationKey: mutationKeyRiskDecideApproval(),
    mutationFn: function riskDecideApprovalMutationFn({
      request,
      security,
      options,
    }): Promise<RiskDecideApprovalMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(riskApprovalsDecide(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
